import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CustomReportsApi } from 'api/custom-reports';
import { WidgetAPI } from 'api/dashboard';
import { getDashboardQueryKey, getDashboardQueryKeyV2 } from './dashboard';
const getResolveWidgetQueryKey = (widgetId)=>[
        'resolve-widget',
        widgetId
    ];
export const getQueryWidgetKey = (widgetId)=>[
        'widget',
        widgetId
    ];
export const getQueryWidgetsBySourceKey = (sourceType)=>[
        'widgets',
        sourceType
    ];
export const getQueryWidgetKeyForDrillDown = (widgetId)=>[
        'widget-drilldown',
        widgetId
    ];
export const getResolveMultipleWidgetsQueryKey = (widgetIds, simpleSearchFilter = null)=>[
        'resolve-multiple',
        ...widgetIds,
        simpleSearchFilter
    ];
export const getResolveMultipleWidgetsQueryKeyV1 = (payload)=>[
        'resolve-multiple-v1',
        payload
    ];
export const getResolveMultipleWidgetsQueryKeyV2 = (payload)=>[
        'resolve-multiple-v2',
        payload
    ];
const useResolveWidgetQuery = (widgetId, options = {})=>{
    return useQuery(getResolveWidgetQueryKey(widgetId), ()=>WidgetAPI.resolveWidget(widgetId), {
        ...options,
        retry: 1,
        refetchOnWindowFocus: false
    });
};
const useResolveMultipleWidgetsQuery = (dashboardId, widgetIds = [], simpleSearchFilter = null, options = {})=>{
    return useQuery(getResolveMultipleWidgetsQueryKey(widgetIds, simpleSearchFilter), ()=>WidgetAPI.resolveMultipleWidgets(dashboardId, widgetIds, simpleSearchFilter), {
        ...options,
        retry: 1,
        refetchOnWindowFocus: false
    });
};
const useResolveMultipleWidgetsQueryV1 = (payload, options = {})=>{
    return useQuery(getResolveMultipleWidgetsQueryKeyV1(payload), ()=>WidgetAPI.resolveMultipleWidgetsV1(payload), {
        ...options,
        retry: 1,
        refetchOnWindowFocus: false
    });
};
const useResolveMultipleWidgetsQueryV2 = (payload, options = {})=>{
    return useQuery(getResolveMultipleWidgetsQueryKeyV2(payload), ()=>WidgetAPI.resolveMultipleWidgetsV2(payload), {
        ...options,
        retry: 1,
        refetchOnWindowFocus: false
    });
};
const useWidgetQuery = (widgetId, options = {})=>{
    return useQuery(getQueryWidgetKey(widgetId), ()=>WidgetAPI.getWidget(widgetId), {
        ...options
    });
};
const useWidgetQueryDashboard = (widgetId, options = {})=>{
    return useQuery(getQueryWidgetKey(widgetId), ()=>WidgetAPI.getWidget(widgetId).then((widget)=>widget), {
        ...options
    });
};
export const previewWidgetQueryKey = ()=>[
        'preview-widget'
    ];
export const previewWidgetQueryKeyV1 = ()=>[
        'preview-widget-v1'
    ];
const usePreviewWidgetQuery = (payload)=>{
    return useQuery([
        previewWidgetQueryKey(),
        payload
    ], ()=>WidgetAPI.previewWidget(payload));
};
const usePreviewWidgetQueryV1 = (payload, options)=>{
    var _ref;
    return useQuery([
        previewWidgetQueryKeyV1(),
        payload
    ], ()=>WidgetAPI.previewWidgetV1(payload), {
        enabled: (_ref = options === null || options === void 0 ? void 0 : options.enabled) !== null && _ref !== void 0 ? _ref : true
    });
};
const useDrillWidgetMutation = (widgetId)=>{
    return useMutation((payload)=>WidgetAPI.drillWidget(widgetId, payload));
};
const useDrillWidgetMutationV2 = (widgetId)=>{
    return useMutation((payload)=>WidgetAPI.drillWidgetV2(widgetId, payload));
};
const useUpdateWidgetOrderMutation = (dashbaordId, options = {})=>useMutation((payload)=>WidgetAPI.updateWidgetOrder(dashbaordId, payload), {
        ...options
    });
const useUpdateWidgetOrderBySourceMutation = (sourceType, options = {})=>useMutation((payload)=>WidgetAPI.updateWidgetOrderBySourceId(sourceType, payload), {
        ...options
    });
const useCreateWidgetMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>WidgetAPI.create(payload).then((widget)=>widget), {
        onSuccess: async (newWidget, { dashboardId })=>{
            const key = getDashboardQueryKey(Number(dashboardId));
            await queryClient.invalidateQueries(key);
        }
    });
};
const useCreateWidgetMutationV2 = ()=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>WidgetAPI.create(payload, true).then((widget)=>widget), {
        onSuccess: async (newWidget, { dashboardId })=>{
            const key = getDashboardQueryKeyV2(Number(dashboardId));
            await queryClient.invalidateQueries(key);
        }
    });
};
const useEditWidgetMutation = (isEditingColumnOrder = false)=>{
    const queryClient = useQueryClient();
    return useMutation(({ widgetId, ...payload })=>WidgetAPI.editWidget(widgetId, payload).then((widget)=>widget), {
        onSuccess: async (res, variables)=>{
            if (isEditingColumnOrder) {
                await queryClient.refetchQueries(getQueryWidgetKeyForDrillDown(res.widgetId));
            } else {
                // Use dashboardId from variables - refetch immediately to prevent race conditions
                await queryClient.refetchQueries(getDashboardQueryKeyV2(variables.dashboardId || 0));
            }
        }
    });
};
const useEditWidgetMutationDashboard = (isEditingColumnOrder = false)=>{
    const queryClient = useQueryClient();
    return useMutation(({ widgetId, ...payload })=>WidgetAPI.editWidget(widgetId, payload).then((widget)=>widget), {
        onSuccess: async (res, variables)=>{
            if (isEditingColumnOrder) {
                await queryClient.refetchQueries(getQueryWidgetKeyForDrillDown(res.widgetId));
            } else {
                // Use dashboardId from variables - refetch immediately to prevent race conditions
                await queryClient.refetchQueries(getDashboardQueryKey(variables.dashboardId || 0));
                await queryClient.refetchQueries(getDashboardQueryKeyV2(variables.dashboardId || 0));
            }
        }
    });
};
const useDeleteWidgetMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation(({ widgetId })=>WidgetAPI.deleteWidget(widgetId), {
        onSuccess: async (_, { dashboardId })=>{
            const key = getDashboardQueryKeyV2(Number(dashboardId));
            const keyV1 = getDashboardQueryKey(Number(dashboardId));
            await queryClient.invalidateQueries(key);
            await queryClient.invalidateQueries(keyV1);
        }
    });
};
const useWidgetExportsMutation = (widgetId, options = {})=>{
    return useMutation((payload)=>WidgetAPI.drillDownExport(widgetId, payload), {
        ...options
    });
};
const useDrillDownColumnOrderQuery = (widgetId, options = {})=>{
    return useQuery(getQueryWidgetKeyForDrillDown(widgetId), ()=>WidgetAPI.getDrillDownColumnOrder(widgetId), {
        ...options
    });
};
// fetch widgets which are not part of dashboards
const useGetAllWidgetsBySource = (sourceType, options = {})=>useQuery(getQueryWidgetsBySourceKey(sourceType), ()=>WidgetAPI.getWidgetsBySource(sourceType), options);
const getWidgetFieldsQueryKey = (sourceType, templateIds)=>[
        'widget-fields',
        sourceType,
        templateIds
    ];
const getWidgetPivotFieldsQueryKey = (sourceType, templateIds)=>[
        'widget-pivot-fields',
        sourceType,
        templateIds
    ];
const useWidgetFieldsQuery = (sourceType, options = {}, templateIds)=>{
    var _options_enabled;
    return useQuery(getWidgetFieldsQueryKey(sourceType, templateIds), async ()=>{
        const fieldsData = await CustomReportsApi.getFields(sourceType, templateIds);
        // Apply the same transformation as custom reports
        const result = Object.keys(fieldsData).reduce((acc, curr)=>{
            const groupFields = fieldsData[curr];
            // Preserve server-provided operation as-is (allow AVG)
            acc[curr] = groupFields;
            return acc;
        }, {});
        return result;
    }, {
        ...options,
        enabled: !!sourceType && ((_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : true),
        retry: 1,
        refetchOnWindowFocus: false
    });
};
const useWidgetPivotFieldsQuery = (sourceType, options = {}, templateIds)=>{
    var _options_enabled;
    return useQuery(getWidgetPivotFieldsQueryKey(sourceType, templateIds), ()=>CustomReportsApi.getPivotFields(sourceType, templateIds), {
        ...options,
        enabled: !!sourceType && ((_options_enabled = options.enabled) !== null && _options_enabled !== void 0 ? _options_enabled : true),
        retry: 1,
        refetchOnWindowFocus: false
    });
};
export { useCreateWidgetMutation, useCreateWidgetMutationV2, useDeleteWidgetMutation, useResolveWidgetQuery, useResolveMultipleWidgetsQuery, useResolveMultipleWidgetsQueryV1, useResolveMultipleWidgetsQueryV2, useWidgetQuery, useWidgetQueryDashboard, useEditWidgetMutation, useEditWidgetMutationDashboard, useDrillWidgetMutation, getResolveWidgetQueryKey, usePreviewWidgetQuery, usePreviewWidgetQueryV1, useDrillWidgetMutationV2, useUpdateWidgetOrderMutation, useUpdateWidgetOrderBySourceMutation, useWidgetExportsMutation, useDrillDownColumnOrderQuery, useGetAllWidgetsBySource, useWidgetFieldsQuery, useWidgetPivotFieldsQuery };
